import { useState, useCallback } from 'react';
import { NovedadesService } from '../service/NovedadesService';
import { EventosService } from '../service/EventosService';
import { toast } from 'react-toastify';

// Hook personalizado para manejar el reporte de novedades en el flujo OnRoad
export const useOnRoadReporting = () => {
    const [isReporting, setIsReporting] = useState(false);

    // Reporta un evento del flujo de remesas con doble reporte (sistema interno + novedades)
    const reportarEventoFlujo = useCallback(async ({ 
        fase, 
        evento, 
        observaciones, 
        evidencias, 
        coords, 
        manifiesto,
        operacionData,
        remesa = null
    }) => {
        if (isReporting) return { success: false, message: 'Ya hay un reporte en progreso' };

        setIsReporting(true);
        
        const toastId = toast.info(`Reportando ${evento} ${fase}...`, {
            autoClose: false,
            closeButton: false
        });

        try {
            const resultado = await EventosService.reportarEvento({
                manifiesto,
                evento,
                fase,
                observaciones: observaciones || `${evento} ${fase} reportado desde OnRoad`,
                coords,
                evidencias: evidencias || [],
                remesa,
                operacionData 
            });

            toast.dismiss(toastId);
            if (resultado.success) {
                return resultado;
            } else {
                const errorMessage = resultado.message || 'Error al reportar evento';
                toast.error(errorMessage, { autoClose: 4000 });
                return resultado;
            }
        } catch (error) {
            console.error('Error inesperado en useOnRoadReporting:', error);
            toast.dismiss(toastId);
            
            const errorMessage = error.message || 'Error inesperado al reportar evento';
            toast.error(errorMessage, { autoClose: 4000 });
            
            return { 
                success: false, 
                message: errorMessage,
                error
            };
        } finally {
            setIsReporting(false);
        }
    }, [isReporting]);

    // Reportar Normalidad
    const reportarNormalidad = useCallback(async (observaciones) => {
        if (isReporting) return { success: false, message: 'Ya hay un reporte en progreso' };

        setIsReporting(true);
        
        const toastId = toast.info("Enviando normalidad...", {
            autoClose: false,
            closeButton: false
        });

        try {
            const resultado = await NovedadesService.reportarNormalidad(observaciones);

            toast.dismiss(toastId);

            if (resultado.success) {
                toast.success(resultado.message, {
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return resultado;
            } else {
                const errorMessage = resultado.message || 'Error al reportar normalidad';
                toast.error(errorMessage, { autoClose: 4000 });
                return resultado;
            }

        } catch (error) {
            toast.dismiss(toastId);
            toast.error('Error inesperado al reportar normalidad', { autoClose: 4000 });
            return { 
                success: false, 
                message: 'Error inesperado al reportar normalidad',
                error 
            };
        } finally {
            setIsReporting(false);
        }
    }, [isReporting]);

    // Reportar Novedades
    const reportarNovedades = useCallback(async ({ novedades, observaciones, evidencia }) => {
        if (isReporting) return { success: false, message: 'Ya hay un reporte en progreso' };

        setIsReporting(true);
        
        const tipoTexto = Array.isArray(novedades) && novedades.length > 1 ? 'novedades' : 'novedad';
        const toastId = toast.info(`Enviando ${tipoTexto}...`, {
            autoClose: false,
            closeButton: false
        });

        try {
            const resultado = await NovedadesService.reportarNovedades({
                novedades,
                observaciones,
                evidencia
            });

            toast.dismiss(toastId);

            if (resultado.success) {
                toast.success(resultado.message, {
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return resultado;
            } else {
                const errorMessage = resultado.message || 'Error al reportar novedad(es)';
                toast.error(errorMessage, { autoClose: 4000 });
                return resultado;
            }

        } catch (error) {
            toast.dismiss(toastId);
            toast.error('Error inesperado al reportar novedad(es)', { autoClose: 4000 });
            return {
                success: false,
                message: 'Error inesperado al reportar novedad(es)',
                error 
            };
        } finally {
            setIsReporting(false);
        }
    }, [isReporting]);

    return {
        isReporting,
        reportarEventoFlujo,
        reportarNormalidad,
        reportarNovedades
    };
}